import classNames from "classnames";
import * as React from "react";
import { ThemeColorType } from "~/types/theme-color";
import { ImageTextCtaReusableProps } from "~/components/image-text-cta/image-text-cta";
import ArrowLink from "../../arrow-link/arrow-link";
import CtaButton from "../../cta-button/cta-button";
import ResponsiveImage from "../../responsive-image/responsive-image";

export interface ImageTextCtaListItemProps extends ImageTextCtaReusableProps {
    bgColor?: ThemeColorType;
    textColor?: ThemeColorType;
    showCtaAsLinkColor?: ThemeColorType; // Can be converted to ctaTextColor later, if Gyldendal want cta-colors to be configurable
    defaultSize?: number;
    srcSetSizes?: number[];
    sizes?: string;
}

const LinkOrFragment = (props: {
    children: React.ReactNode;
    href: ImageTextCtaListItemProps["ctaUrl"];
    target: ImageTextCtaListItemProps["ctaUrl"];
    linkClassName?: string;
}) => {
    return props.href ? (
        <a className={props.linkClassName} href={props.href} target={props.target}>
            {props.children}
        </a>
    ) : (
        <>{props.children}</>
    );
};

const ImageTextCtaListItem = ({
    imageSrc,
    imageAltText,
    title,
    richText,
    ctaLink,
    ctaUrl,
    ctaTarget,
    showCtaAsLink,
    useCtaAsImageLink,
    bgColor,
    textColor,
    showCtaAsLinkColor,
    defaultSize,
    srcSetSizes,
    sizes
}: ImageTextCtaListItemProps) => {
    const baseClass = "image-text-cta-list-item";
    const rootClasses = classNames(baseClass, "config-column");
    const arrowLinkClasses = classNames(`${baseClass}__link`, {
        [`theme-color-${showCtaAsLinkColor}`]: !!showCtaAsLinkColor
    });
    const titleClasses = classNames(`${baseClass}__title h3-style`, {
        [`theme-color-${textColor}`]: !!textColor
    });
    const contentClasses = classNames(`${baseClass}__content-wrapper`, {
        [`theme-bg-${bgColor}`]: !!bgColor,
        [`theme-color-${textColor}`]: !!textColor
    });
    const hasContent = title || richText || ctaLink;

    return (
        <div className={rootClasses}>
            {imageSrc && defaultSize && (
                <div className={`${baseClass}__image-wrapper`}>
                    <LinkOrFragment href={ctaUrl} target={ctaTarget}>
                        <ResponsiveImage
                            srcUrl={imageSrc}
                            alt={imageAltText}
                            defaultSize={defaultSize}
                            srcSetSizes={srcSetSizes}
                            sizes={sizes}
                        />
                    </LinkOrFragment>
                </div>
            )}

            {hasContent && (
                <div className={contentClasses}>
                    {title && (
                        <h3 className={titleClasses}>
                            <LinkOrFragment linkClassName="reset-a" href={ctaUrl} target={ctaTarget}>
                                {title}
                            </LinkOrFragment>
                        </h3>
                    )}
                    {richText && (
                        <div
                            className={`${baseClass}__rich-text content-rich-text`}
                            dangerouslySetInnerHTML={{ __html: richText }}
                        />
                    )}
                    {ctaLink && !useCtaAsImageLink && (
                        <>
                            {showCtaAsLink ? (
                                <ArrowLink className={arrowLinkClasses} link={ctaLink} />
                            ) : (
                                <CtaButton className={`${baseClass}__button`} link={ctaLink} isLarge />
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ImageTextCtaListItem;
